import appdata from '../Services/appdata';
import {
  API_ROOT,
  fetchWithSession,
  dispatchGetJSONAsync,
  dispatchPostJSONAsync,
  completeJSONAsync,
  API_ASYNC_LONG_POLL_TIMEOUT,
  API_ASYNC_LONG_POLL_COUNT,
} from './BaseApi';

async function getJSON(url, username_or_token, password) {
  try {
    const response = await fetchWithSession(url, { method: 'GET' }, username_or_token, password);
    if (!response.ok) {
      return { status: response.status, message: response.status === 401
        ? 'Your R4D login has expired. Please sign in again.'
        : `R4D request failed (HTTP ${response.status}).` };
    }
    return await response.json();
  } catch (error) {
    console.error(error);
    return null;
  }
}

/*************************   Manage Report Data    ****************************/

async function getSelectedAccountingApi() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  if (!company_org_id || company_org_id === -1) return null;
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/get_selected_accounting_api`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getSynergyProjectCustomFields(companyOrgId) {
  const company = appdata.getItem('company');
  const selectedCompanyOrgId = companyOrgId || appdata.getItem('company_org_id');
  if (!company || !selectedCompanyOrgId || selectedCompanyOrgId === -1) {
    return { status: 500, message: 'Please select a company before loading Synergy custom fields.' };
  }

  const url = `${API_ROOT}/managereportdata/${company.id}/${selectedCompanyOrgId}/synergy/project_custom_fields`;
  const task = await dispatchGetJSONAsync(url);
  if (!task) {
    return { status: 503, message: 'R4D could not retrieve the Synergy custom fields.' };
  }

  return await completeJSONAsync(task);
}

async function getSynergyProjectList(companyOrgId) {
  const company = appdata.getItem('company');
  const selectedCompanyOrgId = companyOrgId || appdata.getItem('company_org_id');
  if (!company || !selectedCompanyOrgId || selectedCompanyOrgId === -1) {
    return { status: 500, message: 'Please select a company before downloading the Synergy project list.' };
  }

  const url = `${API_ROOT}/managereportdata/${company.id}/${selectedCompanyOrgId}/synergy/project_list`;
  const task = await dispatchGetJSONAsync(url);
  if (!task) {
    return { status: 503, message: 'R4D could not retrieve the Synergy project list.' };
  }

  return await completeJSONAsync(task);
}

async function checkXeroConnection(companyOrgId) {
  const company = appdata.getItem('company');
  const selectedCompanyOrgId = companyOrgId || appdata.getItem('company_org_id');
  if (!company || !selectedCompanyOrgId || selectedCompanyOrgId === -1) {
    return { status: 500, message: 'Please select a company before refreshing Xero data.' };
  }

  const url = `${API_ROOT}/managereportdata/${company.id}/${selectedCompanyOrgId}/check_xero_connection`;
  const task = await dispatchGetJSONAsync(url);
  if (!task) {
    return {
      status: 503,
      connected: false,
      requires_reconnect: true,
      message: 'R4D could not check the Xero connection. Please reconnect Xero in Company Settings and try again.',
    };
  }

  const result = await completeJSONAsync(task);
  return result || {
    status: 503,
    connected: false,
    requires_reconnect: true,
    message: 'R4D could not check the Xero connection. Please reconnect Xero in Company Settings and try again.',
  };
}

async function oauth(api_type) {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/${api_type}/oauth`;
  return await getJSON(url);
}

async function deltekOauth(customerId, password) {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const user = appdata.getItem('user');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/${user.id}/deltek/oauth`;
  
  let task = await dispatchPostJSONAsync(url, {
    customerId,
    password,
  });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getCompanies() {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/companies`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getCompany(company_id) {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_id}/company`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getOrganizations() {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/organizations`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getDates() {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/datePrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getProjects() {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/projectPrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getClients() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/clientPrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getProjectTypes() {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/projecttypePrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getProjectSubTypes() {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/projectsubtypePrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getEmployees() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/employeePrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getChartOfAccouts() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/chartofaccountPrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getBsAccountTypes() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/bs/accounttype`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getPnlAccountTypes() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/pnl/accounttype`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getBsAccountSubTypes() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/bs/accountsubtype`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getPnlAccountSubTypes() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/pnl/accountsubtype`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getKeyPerformanceIndicators() {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/keyperformanceindicatorPrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function autoFillFyKPIs(fy_year) {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/keyperformanceindicatorPrimary/autofy`;
  let task = await dispatchPostJSONAsync(url, { fy_year });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function getOpportunities() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/opportunityPrimary`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

//GeneralLedger, Employee, Client, KeyPerformanceIndicator
async function getTemplate(tableName) {  
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');

  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/` + l_tableName;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function refreshTemplate(
  tableName,
  apiType,
  selectedCompany,
  selectedOrganisation,
  selectedDate,
  roleCheck,
  groupCheck,
  options = {},
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '_');
  const company = await appdata.getItem('company');

  const url =
    `${API_ROOT}/managereportdata/${company.id}/${selectedCompany}/${selectedOrganisation}/${selectedDate}/` +
    'refresh_' +
    l_tableName;
  let task = await dispatchPostJSONAsync(url, {
    tableName,
    apiType,
    roleCheck,
    groupCheck,
    ...options,
  });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(
    task,
    undefined,
    undefined,
    apiType === 'Synergy'
      ? { pollTimeout: API_ASYNC_LONG_POLL_TIMEOUT, pollCount: API_ASYNC_LONG_POLL_COUNT }
      : {},
  );
}

async function importDataFromExcel(
  tableName,
  updated_data,
  selected_dates,
  company_id,
  org_id,
  options = {},
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = await appdata.getItem('company');
  // const company_org_id = appdata.getItem('company_org_id')
  const url = `${API_ROOT}/managereportdata/${company.id}/import_${l_tableName}`;
  let task = await dispatchPostJSONAsync(url, {
    updated_data,
    selected_dates,
    company_id,
    org_id,
    organisation_id: org_id,
    ...options,
  });
  if (!task) {
    return { status: 500, message: 'Failed to import data' };
  }
  return await completeJSONAsync(task);
}

async function importDataFromCinch(
  tableName,
  updated_data,
  company_id,
  org_id,
  selected_dates,
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = await appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/import/cinch/${l_tableName}`;
  let task = await dispatchPostJSONAsync(url, {
    updated_data,
    company_id,
    org_id,
    selected_dates,
  });
  if (!task) {
    return { status: 500, message: 'Failed to import data' };
  }
  return await completeJSONAsync(task);
}

async function importEmployeeHolidays(
  employees,
  holidays,
  company_id,
  org_id,
) {
  const company = await appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/import_employeeholiday`;
  let task = await dispatchPostJSONAsync(url, {
    employees,
    holidays,
    company_id,
    org_id,
  });
  if (!task) {
    return { status: 500, message: 'Failed to import data' };
  }
  return await completeJSONAsync(task);
}

async function importDataFromExcelForBFC(
  tableName,
  updated_data,
  selectedYear,
  forecast_type,
  selectedOrganisation,
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = await appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url =
    `${API_ROOT}/managereportdata/${company.id}/${selectedYear}/` +
    'import_' +
    l_tableName;
  let task = await dispatchPostJSONAsync(url, {
    forecast_type,
    company_org_id,
    selectedOrganisation,
    updated_data,
  });
  if (!task) {
    return { status: 500, message: 'Failed to import data' };
  }
  return await completeJSONAsync(task);
}

async function updateTemplate(tableName, updated_data) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');

  if (
    tableName === 'PNL' ||
    tableName === 'Report Page Selection' ||
    tableName === 'BalanceSheetLayout'
  ) {
    const url =
      `${API_ROOT}/managereportdata/${company.id}/` + l_tableName + '/update';
    let task = await dispatchPostJSONAsync(url, {
      company_org_id,
      updated_data,
    });
    if (!task) {
      return [];
    }
    return await completeJSONAsync(task);
  } else {    
    const url =
      `${API_ROOT}/managereportdata/${company.id}/` + l_tableName + '/update';
    let task = await dispatchPostJSONAsync(url, {
      updated_data,
    });
    if (!task) {
      return 'failed';
    }
    return await completeJSONAsync(task);
  }
}

async function updateKpiSort(updated_data) {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/keyperformanceindicator/update_sort`;
  let task = await dispatchPostJSONAsync(url, {
    updated_data,
  });
  if (!task) {
    return 'failed';
  }
  return await completeJSONAsync(task);
}

async function addAutoTemplate(
  tableName,
  selectedCompany,
  selectedOrganisation,
  selectedDate,
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = appdata.getItem('company');
  const url =
    `${API_ROOT}/managereportdata/${company.id}/` + l_tableName + '/autoadd';
  let task = await dispatchPostJSONAsync(url, {
    selectedCompany,
    selectedOrganisation,
    selectedDate,
  });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function addTemplate(
  tableName,
  selectedCompany,
  selectedOrganisation,
  selectedDate,
  selectedYear = 2020,
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = appdata.getItem('company');
  const url =
    `${API_ROOT}/managereportdata/${company.id}/` + l_tableName + '/add';
  let task = await dispatchPostJSONAsync(url, {
    selectedCompany,
    selectedOrganisation,
    selectedDate,
    selectedYear,
  });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function addDefaultKpis(add_default_kpis) {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/keyperformanceindicator/default_add`;
  let task = await dispatchPostJSONAsync(url, {
    add_default_kpis,
  });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function addBudgetForecastCashFlow(
  tableName,
  selectedCompany,
  selectedOrganisation,
  selectedDate,
  selectedYear = 2020,
  selectedTempId,
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = appdata.getItem('company');
  const url =
    `${API_ROOT}/managereportdata/${company.id}/` + l_tableName + '/add';
  let task = await dispatchPostJSONAsync(url, {
    selectedCompany,
    selectedOrganisation,
    selectedDate,
    selectedYear,
    selectedTempId,
  });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function deleteTemplate(tableName, rowIndexes) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = appdata.getItem('company');
  const url =
    `${API_ROOT}/managereportdata/${company.id}/` + l_tableName + '/delete';
  let task = await dispatchPostJSONAsync(url, { indexes: rowIndexes });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function deleteAllTemplate(tableName, forecast_type, selectedYear, selectedCompany) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '');
  const company = appdata.getItem('company');
  const url =
    `${API_ROOT}/managereportdata/${company.id}/${selectedYear}/` +
    'deleteall/' +
    l_tableName;
  let task = await dispatchPostJSONAsync(url, { forecast_type, selectedCompany });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function copyFromBudget(type, selectedYear) {
  const company = appdata.getItem('company');
  const url = `${API_ROOT}/managereportdata/${company.id}/budgetforecastcashflow/copy`;
  let task = await dispatchPostJSONAsync(url, { type, selectedYear });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}


async function getSimplifySetting() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/simplify_setting`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function updateSimplifySetting(updated_data) {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/simplify_setting/update`;
  let task = await dispatchPostJSONAsync(url, updated_data);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function checkSimplifyConnection(companyOrgId) {
  const company = appdata.getItem('company');
  const selectedCompanyOrgId = companyOrgId || appdata.getItem('company_org_id');
  if (!company || !selectedCompanyOrgId || selectedCompanyOrgId === -1) {
    return { status: 500, connected: false, message: 'Please select a company before connecting Simplify.' };
  }

  const url = `${API_ROOT}/managereportdata/${company.id}/${selectedCompanyOrgId}/check_simplify_connection`;
  const task = await dispatchGetJSONAsync(url);
  if (!task) {
    return {
      status: 503,
      connected: false,
      message: 'R4D could not test the Simplify connection.',
    };
  }
  const result = await completeJSONAsync(task);
  return result || {
    status: 503,
    connected: false,
    message: 'R4D could not test the Simplify connection.',
  };
}

async function getDeltekSetting() {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const user = appdata.getItem('user');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/deltek/setting/${user.id}`;
  let task = await dispatchGetJSONAsync(url);
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function updateDeltekSetting(updated_data) {
  const company = appdata.getItem('company');
  const company_org_id = appdata.getItem('company_org_id');
  const user = appdata.getItem('user');
  const url = `${API_ROOT}/managereportdata/${company.id}/${company_org_id}/deltek/setting/${user.id}`;
  let task = await dispatchPostJSONAsync(url, { updated_data });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

async function refreshDeltekTemplate(
  tableName,
  selectedCompany,
  selectedOrganisation,
  selectedDate,
  roleCheck,
  groupCheck,
) {
  let l_tableName = tableName.toLowerCase();
  l_tableName = l_tableName.replace(/ |\//g, '_');
  const company = await appdata.getItem('company');
  const user = await appdata.getItem('user');

  const url =
    `${API_ROOT}/managereportdata/${company.id}/${selectedCompany}/${selectedOrganisation}/${user.id}/${selectedDate}/` +
    'refresh_deltek_' +
    l_tableName;
  let task = await dispatchPostJSONAsync(url, {
    tableName,
    roleCheck,
    groupCheck,
  });
  if (!task) {
    return [];
  }
  return await completeJSONAsync(task);
}

const ApiManageReportData = {
  getSelectedAccountingApi,
  getSynergyProjectCustomFields,
  getSynergyProjectList,
  checkXeroConnection,
  oauth,
  deltekOauth,
  getCompanies,
  getCompany,
  getOrganizations,
  getTemplate,
  refreshTemplate,
  importDataFromExcel,
  importDataFromCinch,
  importEmployeeHolidays,
  importDataFromExcelForBFC,
  updateTemplate,
  updateKpiSort,
  addTemplate,
  addDefaultKpis,
  deleteTemplate,
  deleteAllTemplate,
  getDates,
  getProjects,
  getClients,
  getProjectTypes,
  getProjectSubTypes,
  getEmployees,
  getChartOfAccouts,
  getBsAccountTypes,
  getPnlAccountTypes,
  getBsAccountSubTypes,
  getPnlAccountSubTypes,
  getKeyPerformanceIndicators,
  autoFillFyKPIs,
  getOpportunities,
  addAutoTemplate,
  copyFromBudget,
  addBudgetForecastCashFlow,
  getSimplifySetting,
  updateSimplifySetting,
  checkSimplifyConnection,
  getDeltekSetting,
  updateDeltekSetting,
  refreshDeltekTemplate,
};

export default ApiManageReportData;
